import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { Book } from "../../../shared/models";
import { userFacingError } from "../../../shared/userFacingError";
import {
  defaultVersionNameForLocale,
  TRANSLATION_LOCALES,
  type TranslationLocaleCode,
  buildVersionTranslationConfig,
} from "../../../shared/translationLocales";
import {
  generateProtectedTermId,
  getProtectedTermsFromMetadata,
  seedProtectedTermsFromRosters,
  upsertProperNounMarksIntoTerms,
  withProtectedTermsInMetadata,
  type ProtectedTerm,
  type ProtectedTermPolicy,
} from "../../../shared/protectedTerms";
import { cloudAuthDesktopV2 } from "../../services/cloudAuthDesktopV2";
import {
  pullRemoteOverrideLocal,
  pushLocalBookToCloud,
} from "../../services/desktopV2AutoSync";
import { tipTapJsonToPlainText, tryParseStringifiedTipTapDoc } from "../../../shared/tipTapPlainText";
import { llmProviderForModel } from "../../../shared/llmModels";

type Step = "locale" | "glossary" | "confirm" | "progress" | "done";

type BatchError = { title: string; message: string };

type Props = {
  open: boolean;
  book: Book;
  analysisModel: string;
  onClose: () => void;
  onBookChange: (book: Book) => void;
  onStatus?: (message: string) => void;
};

function sortChapters(book: Book) {
  return [...book.chapters].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

function chapterHasTranslatableText(content: unknown): boolean {
  if (typeof content === "string") {
    const doc = tryParseStringifiedTipTapDoc(content);
    if (doc) return tipTapJsonToPlainText(doc).trim().length > 0;
    return content.trim().length > 0;
  }
  if (content && typeof content === "object") {
    return tipTapJsonToPlainText(content).trim().length > 0;
  }
  return false;
}

export function TranslateBookDialog({
  open,
  book,
  analysisModel,
  onClose,
  onBookChange,
  onStatus,
}: Props) {
  const [step, setStep] = useState<Step>("locale");
  const [targetLocale, setTargetLocale] = useState<TranslationLocaleCode>("es-419");
  const [versionName, setVersionName] = useState(defaultVersionNameForLocale("es-419"));
  const [terms, setTerms] = useState<ProtectedTerm[]>([]);
  const [newSurface, setNewSurface] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number; title: string } | null>(
    null
  );
  const [batchErrors, setBatchErrors] = useState<BatchError[]>([]);
  const [resultSummary, setResultSummary] = useState<string | null>(null);
  const cancelRef = useRef(false);

  const cloudReady = cloudAuthDesktopV2.isSignedIn() && Boolean(book.id?.trim());

  useEffect(() => {
    if (!open) return;
    cancelRef.current = false;
    setStep("locale");
    setTargetLocale("es-419");
    setVersionName(defaultVersionNameForLocale("es-419"));
    setError(null);
    setBusy(false);
    setProgress(null);
    setBatchErrors([]);
    setResultSummary(null);
    setNewSurface("");

    const meta = (book.metadata ?? {}) as Record<string, unknown>;
    let seeded = seedProtectedTermsFromRosters({ metadata: meta });
    seeded = upsertProperNounMarksIntoTerms(
      seeded,
      book.chapters.map((ch) => ch.content)
    );
    setTerms(seeded);
  }, [open, book]);

  useEffect(() => {
    setVersionName(defaultVersionNameForLocale(targetLocale));
  }, [targetLocale]);

  const keepCount = useMemo(() => terms.filter((t) => t.policy === "keep").length, [terms]);

  const updateTerm = useCallback((id: string, patch: Partial<ProtectedTerm>) => {
    setTerms((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  const removeTerm = useCallback((id: string) => {
    setTerms((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addTerm = useCallback(() => {
    const surface = newSurface.trim();
    if (!surface) return;
    setTerms((prev) => {
      if (prev.some((t) => t.surface.toLowerCase() === surface.toLowerCase())) return prev;
      const next: ProtectedTerm = {
        id: generateProtectedTermId(),
        surface,
        aliases: [],
        kind: "other",
        policy: "keep",
      };
      return [...prev, next].sort((a, b) =>
        a.surface.localeCompare(b.surface, undefined, { sensitivity: "base" })
      );
    });
    setNewSurface("");
  }, [newSurface]);

  const persistTermsToBook = useCallback(
    (current: Book, nextTerms: ProtectedTerm[]): Book => {
      const meta = withProtectedTermsInMetadata(
        (current.metadata ?? {}) as Record<string, unknown>,
        nextTerms
      );
      return { ...current, metadata: meta as Book["metadata"], updatedAt: new Date().toISOString() };
    },
    []
  );

  const runTranslation = useCallback(async () => {
    if (!cloudReady || !book.id) {
      setError("Sign in and sync this book to your library before translating.");
      return;
    }
    if (!window.desktopV2Api.translateChapter) {
      setError("Translate is unavailable — rebuild the desktop app (electron:build).");
      return;
    }

    cancelRef.current = false;
    setBusy(true);
    setError(null);
    setBatchErrors([]);
    setStep("progress");
    setProgress({ done: 0, total: book.chapters.length, title: "Preparing…" });

    let working = persistTermsToBook(book, terms);
    onBookChange(working);

    try {
      onStatus?.("Syncing source manuscript…");
      const pushed = await pushLocalBookToCloud(working);
      if (pushed.error) {
        throw new Error(`Could not sync before translating: ${pushed.error}`);
      }

      const name = versionName.trim() || defaultVersionNameForLocale(targetLocale);
      onStatus?.("Creating cloud version…");
      const created = await cloudAuthDesktopV2.createBookVersion(book.id, { name });

      const translationConfig = buildVersionTranslationConfig({
        targetLocale,
        sourceLocale:
          typeof working.metadata?.language === "string" ? working.metadata.language : "en",
        protectedTermSurfaces: terms.map((t) => t.surface),
      });
      await cloudAuthDesktopV2.updateVersionBookConfig(book.id, created.id, {
        translation: translationConfig,
      });

      onStatus?.("Loading new version chapters…");
      const pulled = await pullRemoteOverrideLocal(working);
      if (pulled.error) {
        throw new Error(`Could not load new version: ${pulled.error}`);
      }
      working = pulled.book;

      const remoteVersions = await cloudAuthDesktopV2.listBookVersions(book.id);
      working = {
        ...working,
        metadata: {
          ...working.metadata,
          protectedTerms: terms,
          cloudVersions: remoteVersions,
          currentVersionId: created.id,
        },
        updatedAt: new Date().toISOString(),
      };
      onBookChange(working);

      const toTranslate = sortChapters(working).filter((ch) => chapterHasTranslatableText(ch.content));

      const total = toTranslate.length;
      setProgress({ done: 0, total, title: "Starting…" });

      let authToken: string | undefined;
      try {
        authToken = (await cloudAuthDesktopV2.getValidAccessToken()) ?? undefined;
      } catch {
        authToken = undefined;
      }

      const provider = llmProviderForModel(analysisModel);
      const errors: BatchError[] = [];

      for (let i = 0; i < toTranslate.length; i += 1) {
        if (cancelRef.current) {
          setResultSummary(`Cancelled after ${i} of ${total} chapters.`);
          break;
        }
        const chapter = toTranslate[i];
        setProgress({ done: i, total, title: chapter.title });
        onStatus?.(`Translating ${i + 1} of ${total}: ${chapter.title}`);

        const ordered = sortChapters(working);
        const idx = ordered.findIndex((c) => c.id === chapter.id);
        const adjacentTitles = {
          previous: idx > 0 ? ordered[idx - 1]?.title : undefined,
          next: idx >= 0 && idx < ordered.length - 1 ? ordered[idx + 1]?.title : undefined,
        };

        try {
          const result = await window.desktopV2Api.translateChapter({
            chapterId: chapter.id,
            chapterTitle: chapter.title,
            chapterContent: chapter.content,
            targetLocale,
            sourceLocale:
              typeof working.metadata?.language === "string" ? working.metadata.language : "en",
            protectedTerms: terms,
            provider,
            model: analysisModel,
            adjacentTitles,
            authToken,
          });
          if (result.error) {
            errors.push({ title: chapter.title, message: result.error });
            continue;
          }
          if (!result.translatedContent) {
            errors.push({ title: chapter.title, message: "Empty translation returned." });
            continue;
          }

          working = {
            ...working,
            chapters: working.chapters.map((ch) =>
              ch.id === chapter.id
                ? {
                    ...ch,
                    title: result.translatedTitle?.trim() || ch.title,
                    content: result.translatedContent!,
                    updatedAt: new Date().toISOString(),
                  }
                : ch
            ),
            updatedAt: new Date().toISOString(),
          };
          onBookChange(working);
        } catch (e) {
          errors.push({
            title: chapter.title,
            message: userFacingError(e, "generate"),
          });
        }
      }

      setProgress({ done: total, total, title: "Syncing…" });
      onStatus?.("Syncing translated manuscript…");
      const finalPush = await pushLocalBookToCloud(working);
      if (finalPush.error) {
        errors.push({ title: "Sync", message: userFacingError(finalPush.error, "sync") });
      }

      try {
        await cloudAuthDesktopV2.updateVersionBookConfig(book.id, created.id, {
          translation: buildVersionTranslationConfig({
            targetLocale,
            sourceLocale:
              typeof working.metadata?.language === "string" ? working.metadata.language : "en",
            protectedTermSurfaces: terms.map((t) => t.surface),
            completedAt: new Date().toISOString(),
          }),
        });
      } catch {
        /* non-fatal */
      }

      setBatchErrors(errors);
      const okCount = total - errors.filter((e) => e.title !== "Sync").length;
      setResultSummary(
        cancelRef.current
          ? `Cancelled. ${okCount} chapter(s) translated.`
          : errors.length
            ? `Finished with ${errors.length} issue(s). ${okCount} of ${total} chapters translated.`
            : `Translated ${okCount} chapter(s) into ${defaultVersionNameForLocale(targetLocale)}.`
      );
      setStep("done");
      onStatus?.(
        cancelRef.current
          ? "Translation cancelled."
          : `Translation complete · ${defaultVersionNameForLocale(targetLocale)}`
      );
    } catch (e) {
      setError(userFacingError(e, "generate"));
      setStep("confirm");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }, [
    analysisModel,
    book,
    cloudReady,
    onBookChange,
    onStatus,
    persistTermsToBook,
    targetLocale,
    terms,
    versionName,
  ]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        if (busy) cancelRef.current = true;
        else onClose();
      }}
    >
      <DialogContent className="flex max-h-[90vh] w-[min(640px,96vw)] max-w-none flex-col overflow-hidden sm:max-w-none">
        <DialogHeader className="shrink-0 border-b border-border pb-4">
          <DialogTitle>Translate to language</DialogTitle>
          <DialogDescription>
            Creates a new version, freezes the current manuscript, then translates each
            chapter.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto py-4">
          {!cloudReady ? (
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Sign in and save this book to your account to translate.
            </p>
          ) : null}

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          {step === "locale" ? (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="translate-target-locale">Target language</Label>
                <Select
                  value={targetLocale}
                  onValueChange={(value) => setTargetLocale(value as TranslationLocaleCode)}
                >
                  <SelectTrigger id="translate-target-locale" className="w-full" size="default">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSLATION_LOCALES.map((locale) => (
                      <SelectItem key={locale.code} value={locale.code}>
                        {locale.englishLabel} — {locale.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium">Version name</span>
                <Input
                  value={versionName}
                  onChange={(e) => setVersionName(e.target.value)}
                  aria-label="Version name"
                />
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button disabled={!cloudReady} onClick={() => setStep("glossary")}>
                  Next: protected terms
                </Button>
              </div>
            </>
          ) : null}

          {step === "glossary" ? (
            <>
              <p className="text-sm text-muted-foreground">
                These names stay untranslated (or may be transliterated). Seeded from characters,
                locations, and manuscript proper-noun marks. {keepCount} keep term(s).
              </p>
              <div className="flex max-h-[40vh] flex-col gap-2 overflow-auto rounded-md border border-border p-2">
                {terms.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No protected terms yet.</p>
                ) : (
                  terms.map((term) => (
                    <div
                      key={term.id}
                      className="flex flex-wrap items-center gap-2 border-b border-border/60 pb-2 last:border-0"
                    >
                      <Input
                        className="min-w-[8rem] flex-1"
                        value={term.surface}
                        onChange={(e) => updateTerm(term.id, { surface: e.target.value })}
                        aria-label={`Term ${term.surface}`}
                      />
                      <Select
                        value={term.policy}
                        onValueChange={(value) =>
                          updateTerm(term.id, { policy: value as ProtectedTermPolicy })
                        }
                      >
                        <SelectTrigger
                          className="h-8 w-auto text-xs"
                          size="sm"
                          aria-label={`Policy for ${term.surface}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="keep">Keep</SelectItem>
                          <SelectItem value="transliterate">Transliterate</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-[0.72rem] leading-snug text-muted-foreground">{term.kind}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeTerm(term.id)}
                        aria-label={`Remove ${term.surface}`}
                      >
                        Remove
                      </Button>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a protected term…"
                  value={newSurface}
                  onChange={(e) => setNewSurface(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTerm();
                    }
                  }}
                />
                <Button variant="secondary" onClick={addTerm}>
                  Add
                </Button>
              </div>
              <div className="flex justify-between gap-2">
                <Button variant="ghost" onClick={() => setStep("locale")}>
                  Back
                </Button>
                <Button onClick={() => setStep("confirm")}>Next: confirm</Button>
              </div>
            </>
          ) : null}

          {step === "confirm" ? (
            <>
              <p className="text-sm">
                This will <strong>freeze</strong> your current manuscript as a read-only checkpoint
                and create a new editable version named{" "}
                <strong>{versionName.trim() || defaultVersionNameForLocale(targetLocale)}</strong>.
                Then every chapter will be translated to{" "}
                <strong>
                  {TRANSLATION_LOCALES.find((l) => l.code === targetLocale)?.englishLabel}
                </strong>
                .
              </p>
              <p className="text-sm text-muted-foreground">
                Book publishing language stays unchanged until you promote this version to its own
                book (later).
              </p>
              <div className="flex justify-between gap-2">
                <Button variant="ghost" onClick={() => setStep("glossary")} disabled={busy}>
                  Back
                </Button>
                <Button onClick={() => void runTranslation()} disabled={busy || !cloudReady}>
                  Create version &amp; translate
                </Button>
              </div>
            </>
          ) : null}

          {step === "progress" ? (
            <>
              <p className="text-sm">
                {progress
                  ? `${progress.done} of ${progress.total} · ${progress.title}`
                  : "Working…"}
              </p>
              <div className="flex justify-end">
                <Button
                  variant="secondary"
                  onClick={() => {
                    cancelRef.current = true;
                  }}
                >
                  Stop after this chapter
                </Button>
              </div>
            </>
          ) : null}

          {step === "done" ? (
            <>
              <p className="text-sm">{resultSummary}</p>
              {batchErrors.length > 0 ? (
                <ul className="max-h-40 list-disc overflow-auto pl-5 text-sm text-muted-foreground">
                  {batchErrors.map((err) => (
                    <li key={`${err.title}-${err.message}`}>
                      <strong>{err.title}:</strong> {err.message}
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="flex justify-end">
                <Button onClick={onClose}>Done</Button>
              </div>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
